import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import { Interactable, Interactor } from './wle-interaction/index.js';
import Logger from './utils/logger.js';
import { EventsClient } from './events-client.js';
import { EventTypes, Cursor, CursorTarget, FingerCursor } from '@wonderlandengine/components';
import { NetworkedGrabbable } from './networked-grabbable.js';

const MODULE = '3DCursorGrabBridge';

/**
 *  3dcursor-grab-bridge
 */
export class ThreeDcursorGrabBridge extends Component {
    static TypeName = ' 3dcursor-grab-bridge';
    
    private static DEBUG_3DCURSOR_GRAB_BRIDGE = true;

    /** Time window after an initial grab before we'll honor an onUp as an unGrab.
     * This allows for single-click picking up and dropping for mouse users and click-hold for VR.
     */
    @property.float(1.0)
    public holdOrClickForgiveness = 1.0; // seconds

    private interactable: Interactable | null = null;
    private threedInteractor: Interactor | null = null;
    private isGrabbing: boolean = false;
    private isInVR: boolean = false;
    private lastGrabStartTime: number = 0;

    start() {
        this.interactable = this.object.getComponent(Interactable);
        if (!this.interactable) {
            Logger.error(MODULE, `3DCursorGrabBridge requires Interactable on ${this.object.name}`);
            return;
        }
    }

    onActivate() {        
        this.engine.onXRSessionStart.add(this.onXRSessionStart);
        this.engine.onXRSessionEnd.add(this.onXRSessionEnd);
        
        // Set up cursor-target click handler
        let cursorTarget : CursorTarget = this.object.getComponent('cursor-target') as CursorTarget;
        if (!cursorTarget) {
            cursorTarget = this.object.addComponent('cursor-target') as CursorTarget;
        }
        cursorTarget.onDown.add(this.onDown.bind(this));
        cursorTarget.onUp.add(this.onUp.bind(this));
        this.interactable?.onSelectEnd.add(this.onSelectEnd.bind(this));
        this.isGrabbing = false;
        this.threedInteractor = null;
    }

    onDeactivate() {
        // Clean up XR session listeners
        this.engine.onXRSessionStart.remove(this.onXRSessionStart);
        this.engine.onXRSessionEnd.remove(this.onXRSessionEnd);
        let cursorTarget : CursorTarget = this.object.getComponent('cursor-target') as CursorTarget;
        if (cursorTarget) {
            cursorTarget.onDown.remove(this.onDown.bind(this));
            cursorTarget.onUp.remove(this.onUp.bind(this));
        }
        if (this.interactable) {
            this.interactable.onSelectEnd.remove(this.onSelectEnd.bind(this));
        }
    }

    private onXRSessionStart = () => {
        this.isInVR = true;
    };

    private onXRSessionEnd = () => {
        this.isInVR = false;
        
        // Release any active grab when entering VR
        if (this.isGrabbing) {
            this.endGrab();
        }
    };

    private onDown = (obj3d : Object3D, curs : Cursor | FingerCursor, eventType : EventTypes | undefined) => {
        if (ThreeDcursorGrabBridge.DEBUG_3DCURSOR_GRAB_BRIDGE) {
            console.log(`[3DCursorGrabBridge] onDown event on ${this.object.name}. obj3d: ${obj3d.name} cursor: ${curs instanceof Cursor ? curs.handedness : 'unknown'} eventType: ${eventType?.type}`);
        }
        // If already grabbing, ignore
        if (this.isGrabbing /*|| !this.isInVR*/) {
            if ((Date.now() - this.lastGrabStartTime) < (this.holdOrClickForgiveness * 200)) {
                console.log(`[3DCursorGrabBridge] Ignoring startGrab because we're within the forgiveness window.`);
                return;
            } else {
                const networkedGrabbable = this.object.getComponent(NetworkedGrabbable) as NetworkedGrabbable;
                if (!networkedGrabbable || !networkedGrabbable.isGrabbedLocally) {
                    console.log(`[3DCursorGrabBridge] Forcing end of previous grab before starting new grab.`);
                    this.isGrabbing = false;
                } else {
                    console.log(`[3DCursorGrabBridge] Ignoring startGrab because we're already grabbing.`);
                    return;
                }
            }
        }
        
        // Check distance before allowing grab
        let controller = EventsClient.instance;
        let currentPlayerObject =  controller.activePlayerObject;
        if (currentPlayerObject) {
            const playerPos = currentPlayerObject.getPositionWorld(new Float32Array(3));
            const objPos = this.object.getPositionWorld(new Float32Array(3));
            const distance = Math.sqrt(
                (objPos[0] - playerPos[0]) ** 2 +
                (objPos[1] - playerPos[1]) ** 2 +
                (objPos[2] - playerPos[2]) ** 2
            );
            
            if (distance <= 2.75) {
                if(curs instanceof Cursor && curs.handedness) {
                    this.startGrab(curs.handedness);
                } else {
                    this.startGrab(1); // Default to right hand if no handedness
                }
            }
        }
    };

    private onUp = (obj3d : Object3D, curs : Cursor | FingerCursor, eventType : EventTypes | undefined) => {
        if (ThreeDcursorGrabBridge.DEBUG_3DCURSOR_GRAB_BRIDGE) {
            console.log(`[3DCursorGrabBridge] onUp event on ${this.object.name}. obj3d: ${obj3d.name} cursor: ${curs instanceof Cursor ? curs.handedness : 'unknown'} eventType: ${eventType?.type}`);
        }
/*
        if (!this.isInVR) {
            return;
        }
*/
        // Ignore releases within the forgiveness window after grab start
        const timeSinceGrab = (Date.now() - this.lastGrabStartTime) / 1000;
        if (timeSinceGrab < this.holdOrClickForgiveness) {
            return;
        }
        this.onSelectEnd();
    };

    private onSelectEnd = () => {
        if (ThreeDcursorGrabBridge.DEBUG_3DCURSOR_GRAB_BRIDGE) {
            console.log(`[3DCursorGrabBridge] onSelectEnd called`);
        }

        if (this.isGrabbing) {
            this.endGrab();
        }
    };

    private startGrab(handedness: string | number) {
        if (ThreeDcursorGrabBridge.DEBUG_3DCURSOR_GRAB_BRIDGE) {
            console.log(`[3DCursorGrabBridge] startGrab called with handedness: ${handedness}`);
        }
        
        if (!this.interactable || this.isGrabbing) return;

        if(handedness === 'right' || handedness === 0) {
            this.threedInteractor = EventsClient.instance?.activeRightHandObject?.getComponent(Interactor) as Interactor;
        } else {
            this.threedInteractor = EventsClient.instance?.activeLeftHandObject?.getComponent(Interactor) as Interactor;
        }
        if (this.threedInteractor) {
            this.isGrabbing = true;
            this.lastGrabStartTime = Date.now();
            // Use Interactor's startInteraction method instead of manually notifying
            this.threedInteractor.startInteraction(this.interactable);
        }
    }
    private endGrab() {
        if (ThreeDcursorGrabBridge.DEBUG_3DCURSOR_GRAB_BRIDGE) {
            console.log(`[3DCursorGrabBridge] endGrab called`);
        }
        if (!this.isGrabbing || !this.interactable || !this.threedInteractor) return;

        this.isGrabbing = false;
        
        // Re-enable Grabbable component before calling stopInteraction so it can process the release event
        const networkedGrabbable = this.object.getComponent('networked-grabbable');
        if (networkedGrabbable && 'prepareForRelease' in networkedGrabbable) {
            (networkedGrabbable as any).prepareForRelease();
        }
        
        // Properly end the interaction using Interactor's method
        this.threedInteractor.stopInteraction();
        this.threedInteractor = null;
    }
}
